import { useState, type ReactNode } from 'react'
import {
  ArrowRight, AudioLines, Building2, Cctv, ChevronDown, ChevronRight, Clock3, CreditCard, Lightbulb,
  MonitorPlay, MoonStar, Network, PlugZap, Radar, ScanSearch, Server, Volume2,
} from 'lucide-react'
import { Link } from '../../../router.js'
import {
  BandeauFige, BandeauLecture, LIBELLE_GRAVITE, LIBELLE_NIVEAU, Pastille, TON_ALIM, TON_GRAVITE,
  fmtAge, fmtHeure, fmtNombre, fmtRatio, nomSalle, renommerSalles, useVueSonde, type Ton,
} from '../commun.js'
import type { Lecture } from '../../api.js'
import {
  FAMILLES_DOMAINE, cheminInterne, estDecouvertes, estFige, fmtDuree, estIncidents, estOverview, estZones, incidentsOmbre,
  inconnus, ordonnerZones, questionPourquoi, resumeDomaine, texteFenetre, tonCompteur, tonNiveau, tonSalle,
  type AttentionSonde, type Comptes, type CompteurDirect, type DecouvertesSonde, type FamilleSonde,
  type IncidentSonde, type IncidentsSonde, type OverviewSonde, type ZoneSonde, type ZonesSonde,
} from './modele.js'
import './hub.css'
import { useMenuSonde } from '../menu-sonde.js'
import { useMenuSalle } from '../menu-salle.js'

/**
 * HUB SUPERVISION — le tableau de bord de /infrastructure (21/10/2026).
 *
 * Ce que Hugo voit en ouvrant Technique › Infrastructure : le niveau global
 * et POURQUOI, l'état de Sonde, puis LES SALLES en grandes cartes colorées
 * (ROOM 1, ROOM 2, ROOM 3 d'abord — jamais les anciens noms ni le code nu), ce qui demande attention, les incidents ouverts, les
 * compteurs en direct, les familles et les tuiles vers chaque domaine.
 *
 * Tout est lu dans Sonde par le relais de Gestion, relu toutes les 15 s,
 * en pause quand l'onglet est caché. Sonde injoignable : la dernière donnée
 * reste, en GRIS, sous un bandeau — jamais rouge, jamais « hors ligne ».
 * Un seul code couleur (commun.tsx) ; « — » pour une valeur absente.
 */

export interface DonneesHub {
  ov: { lecture: Lecture<OverviewSonde> | null; precedente: OverviewSonde | null }
  zo: { lecture: Lecture<ZonesSonde> | null; precedente: ZonesSonde | null }
  inc: { lecture: Lecture<IncidentsSonde> | null; precedente: IncidentsSonde | null }
  dec: { lecture: Lecture<DecouvertesSonde> | null; precedente: DecouvertesSonde | null }
}

/** Les quatre lectures du hub. `voit` = droit `sonde.read` ; `voitDetails` = `sonde.details.read` (« À valider »). */
export function useHubSonde(voit: boolean, voitDetails: boolean, intervalMs = 15_000): DonneesHub {
  const ov = useVueSonde(voit ? 'overview' : null, estOverview, intervalMs)
  const zo = useVueSonde(voit ? 'zones' : null, estZones, intervalMs)
  const inc = useVueSonde(voit ? 'incidents?status=active&limit=50' : null, estIncidents, intervalMs)
  const dec = useVueSonde(voit && voitDetails ? 'discovery?status=pending&limit=1' : null, estDecouvertes, 60_000)
  return { ov, zo, inc, dec }
}

const donnee = <T,>(v: { lecture: Lecture<T> | null; precedente: T | null }): T | null =>
  v.lecture?.etat === 'ok' ? v.lecture.data : v.precedente

const enEchec = (l: Lecture<unknown> | null): boolean => !!l && l.etat !== 'ok'

/* ═══════════════════════════════ Le hub ═══════════════════════════════ */

export function HubSupervision({ donnees, voit, voitDetails, peutIntegrations }: {
  donnees: DonneesHub
  /** `null` tant que les droits ne sont pas connus. */
  voit: boolean | null
  voitDetails: boolean
  peutIntegrations: boolean
}) {
  const o = donnee(donnees.ov)
  const z = donnee(donnees.zo)
  const inc = donnee(donnees.inc)
  const dec = donnee(donnees.dec)
  const indispo = enEchec(donnees.ov.lecture)
  const fige = indispo || estFige(o?.collector)
  const zones = z?.zones ?? o?.zones ?? []
  const libelleZone = (code: string | null) => (code ? nomSalle(code, zones.find((x) => x.code === code)?.label ?? null) : null)
  const ombre = incidentsOmbre(o)

  return (
    <>
      <header className="sv-hub-tete">
        <div className="lc-stack sv-hub-tete__titres">
          <h1 className="lc-title"><Radar size={26} aria-hidden="true" /> Infrastructure</h1>
          <p className="lc-subtitle">La supervision technique du club, en direct : les salles, les appareils et ce qui demande attention, lus dans Sonde.</p>
        </div>
        {voit ? (
          <Link className="lc-btn lc-btn--primary sv-hub-tete__soiree" href="/infrastructure/live">
            <MonitorPlay size={16} aria-hidden="true" /> Mode soirée
          </Link>
        ) : null}
      </header>

      {voit === false ? (
        <section className="lc-panel sv-hub-panneau" aria-label="Supervision technique">
          <div className="lc-panel__body">
            <p className="sv-hub-vide">Votre compte ne permet pas de voir la supervision technique (droit « Consulter la supervision »). Les services propres à Gestion restent affichés plus bas.</p>
          </div>
        </section>
      ) : (
        <>
          <EtatGlobal o={o} fige={fige} indispo={indispo} lecture={donnees.ov.lecture} enCours={voit === null || (!o && !donnees.ov.lecture)} peutIntegrations={peutIntegrations} />
          <SectionSalles zones={zones} fige={fige} chargement={!o && !z && !indispo} />
          {o ? <SectionAttention o={o} fige={fige} libelleZone={libelleZone} peutIntegrations={peutIntegrations} /> : null}
          <SectionIncidents inc={inc} lecture={donnees.inc.lecture} fige={fige} ombre={ombre} libelleZone={libelleZone} />
          {o ? <SectionDirect o={o} fige={fige} libelleZone={libelleZone} /> : null}
          {o ? <SectionFamilles familles={o.families} fige={fige} /> : null}
          <SectionDomaines familles={o?.families ?? []} fige={fige} devicesTotal={o?.devices_total ?? null}
            enAttente={voitDetails ? dec?.counts.pending ?? null : null} />
        </>
      )}
    </>
  )
}

/* ═══════════════════════════ Niveau global et état de Sonde ═══════════════════════════ */

function EtatGlobal({ o, fige, indispo, lecture, enCours, peutIntegrations }: {
  o: OverviewSonde | null; fige: boolean; indispo: boolean; lecture: Lecture<OverviewSonde> | null; enCours: boolean; peutIntegrations: boolean
}) {
  const ton: Ton = o ? tonNiveau(o.level, fige) : 'gris'
  const [ouvert, setOuvert] = useState<boolean | null>(null)
  // Déplié d'office quand ce n'est pas vert, sauf sur téléphone : les salles d'abord.
  const large = typeof window !== 'undefined' && !!window.matchMedia?.('(min-width: 760px)').matches
  const deplie = ouvert ?? (o ? o.level !== 'OPERATIONAL' && large : false)
  const nonRaccordee = lecture?.etat === 'non_raccordee'

  return (
    <section className={`lc-panel sv-hub-etat sv-hub-etat--${ton}`} aria-labelledby="sv-hub-niveau">
      {indispo ? <div className="sv-hub-etat__bandeau"><BandeauLecture lecture={lecture} depuis={o?.generated_at ?? null} /></div> : null}
      {o && !indispo ? <div className="sv-hub-etat__bandeau"><BandeauFige collector={o.collector} /></div> : null}
      {o?.mains_outage.active ? (
        <p className="sv-hub-coupure" role="alert">
          <PlugZap size={18} aria-hidden="true" />
          <span>
            <strong>Coupure secteur{o.mains_outage.since ? ` depuis ${fmtHeure(o.mains_outage.since)}` : ''}.</strong>
            {o.mains_outage.affected !== null ? ` ${fmtNombre(o.mains_outage.affected)} appareils touchés.` : ''}
            {o.mains_outage.runtime_min !== null ? ` Autonomie de l’onduleur : ${fmtNombre(Math.round(o.mains_outage.runtime_min))} min.` : ''}
          </span>
        </p>
      ) : null}

      <div className="sv-hub-etat__corps">
        <div className="sv-hub-etat__niveau">
          <span className="sv-hub-kicker">Niveau global</span>
          <h2 className="sv-hub-etat__titre" id="sv-hub-niveau">
            <span className={`sv-hub-point sv-hub-point--${ton}`} aria-hidden="true" />
            {o ? o.level_label : enCours ? 'Lecture de Sonde…' : 'Supervision indisponible'}
          </h2>
          <p className="lc-hint sv-hub-etat__lu">
            {o ? <>Lu à <span className="sv-hub-mono">{fmtHeure(o.generated_at)}</span> · données {fmtAge(o.data_age_s)} · source Sonde</> : nonRaccordee ? (
              <>Sonde n’est pas encore raccordée.{peutIntegrations ? <> <Link href="/settings/integrations/sonde">Intégrations › Sonde</Link></> : null}</>
            ) : '—'}
          </p>
          {o && o.level_reasons.length > 0 ? (
            <div className="sv-hub-pourquoi">
              <button type="button" className="sv-hub-pourquoi__btn" aria-expanded={deplie} onClick={() => setOuvert(!deplie)}>
                {deplie ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
                {questionPourquoi(ton)}
              </button>
              {deplie ? (
                <ul className="sv-hub-pourquoi__liste">
                  {o.level_reasons.map((r, i) => <li key={i}>{renommerSalles(r)}</li>)}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>

        <dl className="sv-hub-faits">
          <Fait titre="Collecteur de Sonde">
            {o ? (
              <Pastille ton={indispo ? 'gris' : fige ? 'gris' : 'ok'}>{indispo ? 'Supervision indisponible' : fige ? 'Figé' : 'En marche'}</Pastille>
            ) : <span className="sv-hub-tiret">—</span>}
            {o ? <span className="lc-hint">battement {fmtAge(o.collector.heartbeat_age_s)}</span> : null}
          </Fait>
          <Fait titre="Ce que Sonde publie">
            {o ? (
              o.shadow_mode
                ? <Pastille ton="warn">Mode ombre</Pastille>
                : <Pastille ton={fige ? 'gris' : 'ok'}>Sonde prévient</Pastille>
            ) : <span className="sv-hub-tiret">—</span>}
            {o?.shadow_summary_fr ? <span className="lc-hint">{renommerSalles(o.shadow_summary_fr)}</span> : null}
          </Fait>
          <Fait titre="Mode">
            <span className="sv-hub-fait__valeur">{o ? o.mode === 'evening' ? 'Soirée' : o.mode === 'rest' ? 'Repos' : '—' : '—'}</span>
            {o && o.operating_mode_setting && o.operating_mode_setting !== 'auto' ? <span className="lc-hint">forcé par le réglage</span> : null}
          </Fait>
          <Fait titre="Incidents ouverts">
            <span className="sv-hub-fait__valeur">{o ? fmtNombre(o.incidents.open) : '—'}</span>
            {o && o.incidents.critical > 0 ? <Pastille ton={fige ? 'gris' : 'err'}>{o.incidents.critical} critique{o.incidents.critical > 1 ? 's' : ''}</Pastille> : null}
          </Fait>
          <Fait titre="Alertes en cours">
            <span className="sv-hub-fait__valeur">{o ? fmtNombre(o.alerts.firing) : '—'}</span>
            {o && o.alerts.shadow_firing > 0 ? <span className="lc-hint">+ {o.alerts.shadow_firing} en ombre, non envoyées</span> : null}
          </Fait>
          <Fait titre="Sources lues">
            <span className="sv-hub-fait__valeur">{o ? fmtRatio(o.sources.ok, o.sources.total) : '—'}</span>
            {o && o.sources.not_configured > 0 ? <span className="lc-hint">{o.sources.not_configured} sans clé</span> : null}
          </Fait>
          <Fait titre="Appareils suivis">
            <span className="sv-hub-fait__valeur">{o ? fmtNombre(o.devices_total) : '—'}</span>
            {o ? <span className="lc-hint">{fmtNombre(o.weighted.online)} en ligne · {fmtNombre(inconnus(o.weighted))} sans donnée récente</span> : null}
          </Fait>
        </dl>
      </div>
    </section>
  )
}

function Fait({ titre, children }: { titre: string; children: ReactNode }) {
  return (
    <div className="sv-hub-fait">
      <dt className="sv-hub-fait__titre">{titre}</dt>
      <dd className="sv-hub-fait__corps">{children}</dd>
    </div>
  )
}

/* ═══════════════════════════════ Les salles ═══════════════════════════════ */

const LEGENDE: { ton: Ton; texte: string }[] = [
  { ton: 'ok', texte: 'Opérationnelle' },
  { ton: 'warn', texte: 'À surveiller' },
  { ton: 'err', texte: 'Panne constatée' },
  { ton: 'off', texte: 'Éteinte comme prévu' },
  { ton: 'maint', texte: 'Maintenance' },
  { ton: 'gris', texte: 'Inconnu ou périmé' },
]

function SectionSalles({ zones, fige, chargement }: { zones: ZoneSonde[]; fige: boolean; chargement: boolean }) {
  const { salles, autres } = ordonnerZones(zones)
  return (
    <section className="sv-hub-section" aria-labelledby="sv-hub-salles">
      <header className="sv-hub-section__tete">
        <h2 className="lc-section-title" id="sv-hub-salles">Les salles</h2>
        <ul className="sv-hub-legende" aria-label="Légende des couleurs">
          {LEGENDE.map((l) => <li key={l.ton}><span className={`sv-hub-point sv-hub-point--${l.ton}`} aria-hidden="true" />{l.texte}</li>)}
        </ul>
      </header>
      {chargement ? (
        <div className="sv-hub-salles">
          {['ROOM 1', 'ROOM 2', 'ROOM 3'].map((n) => (
            <div key={n} className="sv-hub-salle sv-hub-salle--grande sv-hub-salle--gris" aria-busy="true">
              <span className="sv-hub-salle__nom">{n}</span>
              <span className="sv-hub-salle__chiffre">—</span>
              <span className="lc-hint">Lecture de Sonde…</span>
            </div>
          ))}
        </div>
      ) : salles.length > 0 ? (
        <div className="sv-hub-salles">
          {salles.map((z) => <CarteSalle key={z.code} z={z} fige={fige} grande />)}
        </div>
      ) : zones.length === 0 ? (
        <p className="sv-hub-vide">Aucune salle lue pour l’instant.</p>
      ) : null}
      {autres.length > 0 ? (
        <div className="sv-hub-zones">
          {autres.map((z) => <CarteSalle key={z.code} z={z} fige={fige} />)}
        </div>
      ) : null}
    </section>
  )
}

const SEGMENTS: { cle: keyof Comptes | 'inconnus'; ton: Ton; libelle: string; pluriel: string }[] = [
  { cle: 'online', ton: 'ok', libelle: 'en ligne', pluriel: 'en ligne' },
  { cle: 'degraded', ton: 'warn', libelle: 'dégradé', pluriel: 'dégradés' },
  { cle: 'offline', ton: 'err', libelle: 'hors ligne', pluriel: 'hors ligne' },
  { cle: 'off_expected', ton: 'off', libelle: 'éteint comme prévu', pluriel: 'éteints comme prévu' },
  { cle: 'maintenance', ton: 'maint', libelle: 'en maintenance', pluriel: 'en maintenance' },
  { cle: 'inconnus', ton: 'gris', libelle: 'inconnu', pluriel: 'inconnus' },
]

const valeurSegment = (c: Comptes, cle: keyof Comptes | 'inconnus') => (cle === 'inconnus' ? inconnus(c) : c[cle] ?? 0)

/** Barre empilée des états d'appareils (décorative : les chiffres sont écrits à côté). */
export function BarreEtats({ c, fige }: { c: Comptes; fige: boolean }) {
  if (!c || c.total <= 0) return <div className="sv-hub-barre sv-hub-barre--vide" aria-hidden="true" />
  return (
    <div className="sv-hub-barre" aria-hidden="true">
      {SEGMENTS.map((s) => {
        const n = valeurSegment(c, s.cle)
        return n > 0 ? <span key={s.cle} className={`sv-hub-barre__seg sv-hub-barre__seg--${fige ? 'gris' : s.ton}`} style={{ flexGrow: n }} /> : null
      })}
    </div>
  )
}

function CarteSalle({ z, fige, grande = false }: { z: ZoneSonde; fige: boolean; grande?: boolean }) {
  const ton = tonSalle(z, fige)
  const vide = z.counts.total === 0
  const fen = texteFenetre(z)
  const tonAlim: Ton = fige ? 'gris' : TON_ALIM[z.power.situation] ?? 'gris'
  const alim = z.power.situation === 'unknown' ? 'Alimentation inconnue' : z.power.label_fr
  const comptes = SEGMENTS.filter((s) => grande ? s.cle !== 'maintenance' || z.counts.maintenance > 0 : valeurSegment(z.counts, s.cle) > 0)
  const nom = nomSalle(z.code, z.label)
  const menuSalle = useMenuSalle()

  return (
    <Link href={`/infrastructure/salles/${encodeURIComponent(z.code)}`}
      className={`sv-hub-salle sv-hub-salle--${ton} ${grande ? 'sv-hub-salle--grande' : ''} ${fige ? 'sv-hub-salle--fige' : ''}`}
      aria-label={`${nom} : ${z.level ? LIBELLE_NIVEAU[z.level] ?? z.level : 'niveau inconnu'}, ${alim}, score ${z.score ?? 'inconnu'}`}
      {...menuSalle.salle({ code: z.code, label: z.label })}>
      <div className="sv-hub-salle__tete">
        <span className="sv-hub-salle__nom">{nom}</span>
      </div>

      <div className="sv-hub-salle__etats">
        {!vide ? <Pastille ton={tonNiveau(z.level, fige)}>{z.level ? LIBELLE_NIVEAU[z.level] ?? z.level : 'Niveau inconnu'}</Pastille> : null}
        {grande || z.power.situation !== 'unknown'
          ? <Pastille ton={tonAlim} title={z.power.source === 'snapshot' ? 'Dernier état connu' : 'Alimentation de la salle'}>{alim}</Pastille>
          : null}
      </div>

      {vide ? (
        <p className="sv-hub-salle__vide">Aucun appareil de cette salle n’est encore au registre de Sonde : ils attendent dans « À valider ».</p>
      ) : (
        <>
          <div className="sv-hub-salle__score">
            <span className="sv-hub-salle__chiffre">{z.score ?? '—'}</span>
            <span className="sv-hub-salle__sur">/100</span>
            {grande && z.score_detail?.explanation_fr ? <span className="lc-hint sv-hub-salle__expl">{renommerSalles(z.score_detail.explanation_fr)}</span> : null}
          </div>
          <BarreEtats c={z.counts} fige={fige} />
          <ul className="sv-hub-salle__comptes">
            {comptes.map((s) => {
              const n = valeurSegment(z.counts, s.cle)
              return (
                <li key={s.cle} className={n === 0 ? 'sv-hub-compte sv-hub-compte--zero' : 'sv-hub-compte'}>
                  <span className={`sv-hub-point sv-hub-point--${fige ? 'gris' : s.ton}`} aria-hidden="true" />
                  <span className="sv-hub-compte__n">{n}</span>
                  <span>{n > 1 ? s.pluriel : s.libelle}</span>
                </li>
              )
            })}
          </ul>
        </>
      )}

      <div className="sv-hub-salle__pied">
        {z.incidents_open > 0
          ? <Pastille ton={fige ? 'gris' : 'warn'}>{z.incidents_open} incident{z.incidents_open > 1 ? 's' : ''} ouvert{z.incidents_open > 1 ? 's' : ''}</Pastille>
          : grande ? <span className="lc-hint">Aucun incident ouvert</span> : null}
        {fen && grande ? (
          <span className="sv-hub-salle__fenetre">
            <span className="sv-hub-salle__fenetre-titre">{fen.titre}</span>
            <span>{fen.plage}</span>
            {fen.libelle ? <span className={fen.secours ? 'sv-hub-secours' : 'lc-hint'} title={fen.secours ? 'Gestion n’a pas transmis d’horaire à Sonde : elle applique son planning de secours.' : undefined}>{fen.libelle}</span> : null}
          </span>
        ) : null}
        <span className="lc-hint sv-hub-salle__age"><Clock3 size={13} aria-hidden="true" /> {fmtAge(z.data_age_s)}</span>
        {grande ? <span className="sv-hub-salle__cta">Ouvrir la salle <ArrowRight size={14} aria-hidden="true" /></span> : null}
      </div>
    </Link>
  )
}

/* ═══════════════════════════ Ce qui demande attention ═══════════════════════════ */

function SectionAttention({ o, fige, libelleZone, peutIntegrations }: {
  o: OverviewSonde; fige: boolean; libelleZone: (c: string | null) => string | null; peutIntegrations: boolean
}) {
  const items = o.attention.items
  return (
    <section className="lc-panel sv-hub-panneau" aria-labelledby="sv-hub-attention">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="sv-hub-attention">Ce qui demande attention</h2>
        <span className="lc-panel__spacer" />
        <span className="lc-hint">{o.attention.total} point{o.attention.total > 1 ? 's' : ''}</span>
      </header>
      {items.length === 0 ? (
        <div className="lc-panel__body"><p className="sv-hub-vide">Rien ne demande attention{fige ? ' dans la dernière donnée lue' : ''}.</p></div>
      ) : (
        <ul className="sv-hub-liste">
          {items.map((a, i) => <LigneAttention key={`${a.kind}:${a.ref ?? i}`} a={a} fige={fige} zone={libelleZone(a.zone_code)} peutIntegrations={peutIntegrations} />)}
          {o.attention.total > items.length ? <li className="sv-hub-liste__plus lc-hint">… et {o.attention.total - items.length} autre{o.attention.total - items.length > 1 ? 's' : ''}.</li> : null}
        </ul>
      )}
    </section>
  )
}

function LigneAttention({ a, fige, zone, peutIntegrations }: { a: AttentionSonde; fige: boolean; zone: string | null; peutIntegrations: boolean }) {
  const chemin = cheminInterne(a.path)
  const contenu = (
    <>
      <Pastille ton={fige || a.shadow ? 'gris' : TON_GRAVITE[a.severity] ?? 'gris'}>{LIBELLE_GRAVITE[a.severity] ?? a.severity}</Pastille>
      <span className="lc-stack sv-hub-ligne__texte">
        <span className="sv-hub-ligne__titre">{renommerSalles(a.title_fr)}</span>
        {a.detail_fr ? <span className="lc-hint">{renommerSalles(a.detail_fr)}</span> : null}
        {a.shadow ? <span className="lc-hint">Mode ombre : Sonde observe sans alerter.</span> : null}
        {/* La clé des HORAIRES n'est pas un compte d'équipement : Gestion la crée
            elle-même (Accès de service), puis on la colle dans Intégrations › Sonde. */}
        {a.kind === 'source' && a.ref === 'gestion' ? (
          <span className="lc-hint">
            Cette clé se crée dans Gestion : Intégrations › Accès de service › « Créer l’accès », périmètre « Horaires des soirées, pour la supervision technique (Sonde) ».
            Le secret n’est montré qu’une fois : collez-le ensuite dans Intégrations › Sonde › Clés des comptes lecteur › « Gestion ». Sans elle, Sonde suit son planning de secours.
          </span>
        ) : null}
      </span>
      <span className="sv-hub-ligne__meta">
        {zone ? <span>{zone}</span> : null}
        {a.since ? <span className="sv-hub-mono">depuis {fmtHeure(a.since)}</span> : null}
      </span>
    </>
  )
  const classe = `sv-hub-ligne ${a.shadow ? 'sv-hub-ligne--ombre' : ''}`
  if (chemin) {
    return <li><Link className={`${classe} sv-hub-ligne--lien`} href={chemin}>{contenu}<ChevronRight size={16} aria-hidden="true" className="sv-hub-ligne__chev" /></Link></li>
  }
  return (
    <li className={classe}>
      {contenu}
      {a.kind === 'source' && a.ref === 'gestion' && peutIntegrations ? <Link className="lc-btn lc-btn--ghost sv-hub-ligne__action" href="/settings/integrations/service-credentials">Accès de service</Link> : null}
      {a.kind === 'source' && peutIntegrations ? <Link className="lc-btn lc-btn--ghost sv-hub-ligne__action" href="/settings/integrations/sonde">Intégrations › Sonde</Link> : null}
    </li>
  )
}

/* ═══════════════════════════════ Incidents ouverts ═══════════════════════════════ */

function SectionIncidents({ inc, lecture, fige, ombre, libelleZone }: {
  inc: IncidentsSonde | null; lecture: Lecture<IncidentsSonde> | null; fige: boolean; ombre: Set<string>; libelleZone: (c: string | null) => string | null
}) {
  const grise = fige || enEchec(lecture)
  return (
    <section className="lc-panel sv-hub-panneau" aria-labelledby="sv-hub-incidents">
      <header className="lc-panel__head sv-hub-panneau__tete">
        <h2 className="lc-section-title" id="sv-hub-incidents">Incidents ouverts</h2>
        <span className="lc-panel__spacer" />
        {inc ? (
          <span className="lc-hint">
            {inc.counts.open} ouvert{inc.counts.open > 1 ? 's' : ''} · {inc.counts.acknowledged} pris en charge · {inc.counts.recovering} en rétablissement · {inc.counts.resolved_7d} résolu{inc.counts.resolved_7d > 1 ? 's' : ''} sur 7 jours
          </span>
        ) : null}
      </header>
      {enEchec(lecture) ? <div className="sv-hub-etat__bandeau"><BandeauLecture lecture={lecture} /></div> : null}
      {!inc ? (
        <div className="lc-panel__body"><p className="sv-hub-vide">{lecture ? '—' : 'Lecture des incidents…'}</p></div>
      ) : inc.items.length === 0 ? (
        <div className="lc-panel__body"><p className="sv-hub-vide">Aucun incident ouvert.</p></div>
      ) : (
        <ul className="sv-hub-liste">
          {inc.items.map((i) => <LigneIncident key={i.id} i={i} fige={grise} ombre={ombre.has(i.id)} zone={libelleZone(i.zone_code ?? i.root?.zone_code ?? null)} />)}
          {inc.next_cursor ? <li className="sv-hub-liste__plus lc-hint">Les 50 plus récents sont affichés.</li> : null}
        </ul>
      )}
    </section>
  )
}

function LigneIncident({ i, fige, ombre, zone }: { i: IncidentSonde; fige: boolean; ombre: boolean; zone: string | null }) {
  const menu = useMenuSonde()
  return (
    <li>
      <Link className={`sv-hub-ligne sv-hub-ligne--lien ${ombre ? 'sv-hub-ligne--ombre' : ''}`} href={`/infrastructure/incidents/${encodeURIComponent(i.id)}`}
            {...menu.incident({ id: i.id, titre: renommerSalles(i.title), status: i.status, acquitte: i.acked_at !== null, sourdine: i.snoozed_until ?? null })}>
        <Pastille ton={fige || ombre ? 'gris' : TON_GRAVITE[i.severity] ?? 'gris'}>{LIBELLE_GRAVITE[i.severity] ?? i.severity}</Pastille>
        <span className="lc-stack sv-hub-ligne__texte">
          <span className="sv-hub-ligne__titre">{renommerSalles(i.title)}</span>
          <span className="lc-hint">
            {i.status_label}
            {i.root ? ` · ${i.root.display_name}` : ''}
            {i.affected_count > 1 ? ` · ${i.affected_count} appareils touchés` : ''}
            {ombre ? ' · mode ombre, non notifié' : ''}
            {i.snoozed_until ? ` · en sourdine jusqu’à ${fmtHeure(i.snoozed_until)}` : ''}
          </span>
        </span>
        <span className="sv-hub-ligne__meta">
          {zone ? <span>{zone}</span> : null}
          <span className="sv-hub-mono">depuis {fmtHeure(i.opened_at)}</span>
          <span>{fmtDuree(i.duration_s)}</span>
        </span>
        <ChevronRight size={16} aria-hidden="true" className="sv-hub-ligne__chev" />
      </Link>
    </li>
  )
}

/* ═══════════════════════════════ En direct ═══════════════════════════════ */

const VIDE_DIRECT = {
  tpe: 'Aucun TPE n’est encore suivi par Sonde. Les appareils découverts attendent dans « À valider ».',
  cam: 'Aucune caméra ni NVR n’est encore suivi par Sonde. Les appareils découverts attendent dans « À valider ».',
  dmx: 'Aucun nœud lumière n’est encore suivi par Sonde. Les appareils découverts attendent dans « À valider ».',
  son: 'Aucun sonomètre n’est encore suivi par Sonde.',
}

function SectionDirect({ o, fige, libelleZone }: { o: OverviewSonde; fige: boolean; libelleZone: (c: string | null) => string | null }) {
  const l = o.live
  return (
    <section className="sv-hub-section" aria-labelledby="sv-hub-direct">
      <header className="sv-hub-section__tete">
        <h2 className="lc-section-title" id="sv-hub-direct">En direct</h2>
        <span className="lc-hint">Compteurs de confiance : en état / suivis. Un appareil sans donnée récente n’est jamais compté en panne.</span>
      </header>
      <div className="sv-hub-directs">
        <TuileDirect icone={<CreditCard size={18} aria-hidden="true" />} titre="TPE joignables" c={l.payment_terminals} fige={fige} vide={VIDE_DIRECT.tpe} />
        <TuileDirect icone={<Cctv size={18} aria-hidden="true" />} titre="Caméras qui enregistrent" c={l.cameras_recording} fige={fige} vide={VIDE_DIRECT.cam} />
        <TuileDirect icone={<Lightbulb size={18} aria-hidden="true" />} titre="Ports DMX actifs" c={l.dmx_ports_active} fige={fige} vide={VIDE_DIRECT.dmx} />
        <div className="sv-hub-direct">
          <span className="sv-hub-direct__titre"><Volume2 size={18} aria-hidden="true" /> Son (LAeq 15 min)</span>
          {l.sound.length === 0 ? (
            <>
              <span className="sv-hub-direct__valeur sv-hub-tiret">—</span>
              <span className="lc-hint">{VIDE_DIRECT.son}</span>
            </>
          ) : (
            <ul className="sv-hub-sons">
              {l.sound.map((s) => (
                <li key={s.uid} className="sv-hub-son">
                  <span className="sv-hub-son__salle">{libelleZone(s.zone_code) ?? s.uid}</span>
                  <span className="sv-hub-son__valeur">{s.laeq15_db === null ? '—' : `${fmtNombre(Math.round(s.laeq15_db * 10) / 10)} dB(A)`}</span>
                  <span className="lc-hint">{s.limit_a_db === null ? 'limite —' : `limite ${fmtNombre(s.limit_a_db)} dB(A)`} · {fmtAge(s.data_age_s)}</span>
                  {s.minutes_to_limit !== null ? <Pastille ton={fige ? 'gris' : 'warn'}>Limite atteinte dans {fmtNombre(Math.max(0, Math.round(s.minutes_to_limit)))} min</Pastille> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  )
}

function TuileDirect({ icone, titre, c, fige, vide }: { icone: ReactNode; titre: string; c: CompteurDirect; fige: boolean; vide: string }) {
  const ton = fige ? 'gris' : tonCompteur(c)
  const absent = c.ok === null || c.total === null
  return (
    <div className={`sv-hub-direct sv-hub-direct--${ton}`}>
      <span className="sv-hub-direct__titre">{icone} {titre}</span>
      <span className={`sv-hub-direct__valeur ${absent ? 'sv-hub-tiret' : ''}`}>{fmtRatio(c.ok, c.total)}</span>
      {absent ? <span className="lc-hint">{vide}</span> : null}
      {!absent && (c.unobserved ?? 0) > 0 ? <span className="lc-hint">{c.unobserved} sans donnée récente</span> : null}
      {!absent ? <span className="lc-hint"><Clock3 size={13} aria-hidden="true" /> {fmtAge(c.data_age_s)}</span> : null}
    </div>
  )
}

/* ═══════════════════════════════ Familles ═══════════════════════════════ */

function SectionFamilles({ familles, fige }: { familles: FamilleSonde[]; fige: boolean }) {
  if (familles.length === 0) return null
  return (
    <section className="sv-hub-section" aria-labelledby="sv-hub-familles">
      <header className="sv-hub-section__tete">
        <h2 className="lc-section-title" id="sv-hub-familles">Familles d’appareils</h2>
      </header>
      <ul className="sv-hub-familles">
        {familles.map((f) => (
          <li key={f.family} className={`sv-hub-famille sv-hub-famille--${tonNiveau(f.level, fige)}`}>
            <span className="sv-hub-famille__tete">
              <span className={`sv-hub-point sv-hub-point--${tonNiveau(f.level, fige)}`} aria-hidden="true" />
              <span className="sv-hub-famille__nom">{f.label}</span>
            </span>
            <span className="sv-hub-famille__chiffre">{fmtRatio(f.counts.online, f.counts.total)}<span className="lc-hint"> en ligne</span></span>
            <BarreEtats c={f.counts} fige={fige} />
            <span className="lc-hint">
              {f.attention > 0 ? `${f.attention} à surveiller · ` : ''}{f.counts.off_expected > 0 ? `${f.counts.off_expected} éteint${f.counts.off_expected > 1 ? 's' : ''} comme prévu · ` : ''}{fmtAge(f.data_age_s)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

/* ═══════════════════════════════ Domaines ═══════════════════════════════ */

interface Domaine { cle: string; titre: string; desc: string; href: string; icone: ReactNode; familles?: readonly string[] }

const DOMAINES: Domaine[] = [
  { cle: 'reseau', titre: 'Réseau', desc: 'Switchs, bornes Wi-Fi, TPE, Internet', href: '/technique/reseau', icone: <Network size={20} aria-hidden="true" />, familles: FAMILLES_DOMAINE.reseau },
  { cle: 'video', titre: 'Vidéosurveillance', desc: 'Caméras, enregistrement, NVR, Viewports', href: '/securite/videosurveillance', icone: <Cctv size={20} aria-hidden="true" />, familles: FAMILLES_DOMAINE.video },
  { cle: 'av', titre: 'Audiovisuel', desc: 'Lumière, DMX, son', href: '/technique/audiovisuel', icone: <AudioLines size={20} aria-hidden="true" />, familles: FAMILLES_DOMAINE.audiovisuel },
  { cle: 'batiment', titre: 'Bâtiment', desc: 'Onduleur, détecteurs de fumée, énergie, portes (UniFi Access)', href: '/technique/batiment', icone: <Building2 size={20} aria-hidden="true" />, familles: FAMILLES_DOMAINE.batiment },
  { cle: 'serveur', titre: 'Serveur', desc: 'Disques, conteneurs, sauvegardes, certificats', href: '/infrastructure/serveur', icone: <Server size={20} aria-hidden="true" />, familles: FAMILLES_DOMAINE.serveur },
  { cle: 'nuits', titre: 'Nuits', desc: 'Rapport et rejeu de chaque soirée', href: '/infrastructure/nuits', icone: <MoonStar size={20} aria-hidden="true" /> },
  { cle: 'registre', titre: 'Registre et « À valider »', desc: 'Appareils suivis, découvertes à rattacher', href: '/infrastructure/registre', icone: <ScanSearch size={20} aria-hidden="true" /> },
  { cle: 'live', titre: 'Mode soirée', desc: 'Plein écran pour la tablette de la régie', href: '/infrastructure/live', icone: <MonitorPlay size={20} aria-hidden="true" /> },
]

function SectionDomaines({ familles, fige, devicesTotal, enAttente }: {
  familles: FamilleSonde[]; fige: boolean; devicesTotal: number | null; enAttente: number | null
}) {
  const menuSalle = useMenuSalle()
  return (
    <section className="sv-hub-section" aria-labelledby="sv-hub-domaines">
      <header className="sv-hub-section__tete">
        <h2 className="lc-section-title" id="sv-hub-domaines">Par domaine</h2>
      </header>
      <div className="sv-hub-domaines">
        {DOMAINES.map((d) => {
          const r = d.familles ? resumeDomaine(familles, d.familles) : null
          const ton: Ton = r ? tonNiveau(r.level, fige) : 'gris'
          return (
            <Link key={d.cle} href={d.href} className="sv-hub-domaine" {...menuSalle.destination({ href: d.href, titre: d.titre })}>
              <span className="sv-hub-domaine__icone">{d.icone}</span>
              <span className="lc-stack sv-hub-domaine__texte">
                <span className="sv-hub-domaine__titre">{d.titre}</span>
                <span className="lc-hint">{d.desc}</span>
                {d.familles ? (
                  r ? (
                    <span className="sv-hub-domaine__etat">
                      <span className={`sv-hub-point sv-hub-point--${ton}`} aria-hidden="true" />
                      {fmtRatio(r.enLigne, r.total)} en ligne{r.attention > 0 ? ` · ${r.attention} à surveiller` : ''}
                      <span className="sv-hub-domaine__age"><Clock3 size={13} aria-hidden="true" /> {fmtAge(r.age)}</span>
                    </span>
                  ) : (
                    <span className="sv-hub-domaine__etat sv-hub-domaine__etat--vide">
                      <span className="sv-hub-point sv-hub-point--gris" aria-hidden="true" /> Aucun appareil au registre pour l’instant
                    </span>
                  )
                ) : null}
                {d.cle === 'registre' ? (
                  <span className="sv-hub-domaine__etat">
                    {enAttente !== null && enAttente > 0 ? <Pastille ton="warn">{fmtNombre(enAttente)} à valider</Pastille> : null}
                    <span className="lc-hint">{fmtNombre(devicesTotal)} appareils suivis</span>
                  </span>
                ) : null}
              </span>
              <ChevronRight size={16} aria-hidden="true" className="sv-hub-domaine__chev" />
            </Link>
          )
        })}
      </div>
    </section>
  )
}
